import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UserPlus, Trash, ShieldCheck, User } from "@phosphor-icons/react";

const inputCls =
  "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-[#F5A623] focus:ring-1 focus:ring-[#F5A623]";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "user" });
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setMsg(""); setLoading(true);
    try {
      await api.post("/users", form);
      setMsg(`User ${form.email} created.`);
      setForm({ email: "", name: "", password: "", role: "user" });
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-fade-up" data-testid="users-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#F5A623]">Administration</div>
        <h1 className="mt-2 text-4xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          User <span className="text-[#F5A623]">Access Control</span>
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          Only administrators can add or remove accounts. No public registration.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 p-6 rounded-lg bg-zinc-900/50 border border-zinc-800 space-y-4" data-testid="user-create-form">
          <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-2"><UserPlus size={14} /> Add User</div>
          <div className="space-y-3">
            <input type="email" required placeholder="email@kermagames.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="user-email-input" />
            <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name-input" />
            <input type="password" required placeholder="Temporary password (min 6 chars)" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="user-password-input" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-role-input">
              <option value="user">User (read/write)</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
          {err && <div className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded-md px-3 py-2">{err}</div>}
          {msg && <div className="text-sm text-emerald-400 bg-emerald-950/30 border border-emerald-900/50 rounded-md px-3 py-2">{msg}</div>}
          <button type="submit" disabled={loading} data-testid="user-create-btn" className="w-full inline-flex justify-center items-center gap-2 px-4 py-2.5 rounded-md bg-[#F5A623] hover:bg-[#E67E22] text-zinc-950 font-bold text-sm transition-colors disabled:opacity-60">
            <UserPlus size={16} /> {loading ? "Creating…" : "Create User"}
          </button>
        </form>

        <div className="lg:col-span-3 rounded-lg bg-zinc-900/50 border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-zinc-900 border-b border-zinc-800">
              <tr className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left hidden sm:table-cell">Role</th>
                <th className="p-3 text-left hidden md:table-cell">Since</th>
                <th className="p-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-zinc-800/50" data-testid={`user-row-${u.email}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-zinc-800 grid place-items-center text-xs font-bold text-[#F5A623]">
                        {u.name?.charAt(0) || "U"}
                      </div>
                      <div>
                        <div className="text-zinc-100">{u.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden sm:table-cell">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-[#F5A623]/10 text-[#F5A623] border border-[#F5A623]/20">
                        <ShieldCheck size={12} weight="duotone" /> admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs bg-zinc-800 text-zinc-300 border border-zinc-700">
                        <User size={12} /> user
                      </span>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-zinc-500 hidden md:table-cell">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => del(u.id)} data-testid={`delete-user-${u.email}`} className="p-2 rounded text-zinc-500 hover:text-red-400 hover:bg-red-950/30 transition-colors">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-zinc-500 text-sm">No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
